import {
    createFormHook,
    createFormHookContexts,
    type AppFieldExtendedReactFieldGroupApi,
    type FieldsMap,
    type FormAsyncValidateOrFn,
    type FormValidateOrFn,
} from '@tanstack/react-form';

const FIELD_COMPONENTS = {};
const FORM_COMPONENTS = {};

const {
    fieldContext,
    formContext,
    useFormContext: Form_useCtx,
    useFieldContext: Form_useFieldCtx,
} = createFormHookContexts();
const { useAppForm, withForm, withFieldGroup } = createFormHook({
    formContext,
    fieldContext,

    fieldComponents: FIELD_COMPONENTS,
    formComponents: FORM_COMPONENTS,
});

export { useAppForm, withForm, Form_useCtx, Form_useFieldCtx };

export declare namespace FieldGroup_Builder {
    // prettier-ignore
    export type RenderProps<$Builder, $RenderProps> = $Builder extends FieldGroup_Builder<infer $FieldGroupData, infer $SubmitMeta>
        ? $RenderProps & {
            group: AppFieldExtendedReactFieldGroupApi<
                unknown,
                $FieldGroupData,
                string | FieldsMap<unknown, $FieldGroupData>,
                undefined | FormValidateOrFn<unknown>,
                undefined | FormValidateOrFn<unknown>,
                undefined | FormAsyncValidateOrFn<unknown>,
                undefined | FormValidateOrFn<unknown>,
                undefined | FormAsyncValidateOrFn<unknown>,
                undefined | FormValidateOrFn<unknown>,
                undefined | FormAsyncValidateOrFn<unknown>,
                undefined | FormValidateOrFn<unknown>,
                undefined | FormAsyncValidateOrFn<unknown>,
                undefined | FormAsyncValidateOrFn<unknown>,
                $SubmitMeta,
                typeof FIELD_COMPONENTS,
                typeof FORM_COMPONENTS
            >
        }
        : never;

    export type Component<$FieldGroupData, $SubmitMeta, $RenderProps extends object> = ReturnType<
        typeof withFieldGroup<$FieldGroupData, $SubmitMeta, $RenderProps>
    > & {
        defaultValues: $FieldGroupData;
    };

    export type inferSubmitMeta<T> =
        T extends FieldGroup_Builder.Component<any, infer $SubmitMeta, any> ? $SubmitMeta : never;
}
export class FieldGroup_Builder<$FieldGroupData, $SubmitMeta = unknown> {
    public onSubmitMeta: $SubmitMeta | undefined;

    constructor(public readonly defaultValues: $FieldGroupData) {}

    /** Optionally add `onSubmitMeta` */
    public withOnSubmitMeta<$SubmitMeta>(
        meta: $SubmitMeta,
    ): FieldGroup_Builder<$FieldGroupData, $SubmitMeta> {
        this.onSubmitMeta = meta as any;
        return this as any;
    }

    public Component<$RenderProps extends Record<string, unknown> = {}>(
        RenderComponent: React.FC<FieldGroup_Builder.RenderProps<typeof this, $RenderProps>>,
    ): FieldGroup_Builder.Component<$FieldGroupData, $SubmitMeta, $RenderProps> {
        const Component_internal = withFieldGroup<
            NoInfer<$FieldGroupData>,
            NoInfer<$SubmitMeta>,
            NoInfer<$RenderProps>
        >({
            defaultValues: this.defaultValues,
            onSubmitMeta: this.onSubmitMeta,
            render: RenderComponent as any,
        });

        return Object.assign(Component_internal, {
            defaultValues: this.defaultValues,
        });
    }
}
